// Render-only subset of `text-element-mixin.js` used by the *Production
// variants on the canvas. Carries the computeds that produce the visible
// styling + click-action behaviour, and nothing that touches the rich-
// text editor, the link-picker modal, the inline-edit watchers, or the
// inspector chain.
//
// Variants compose this with `DynamicElementMixin` and supply per-type
// `value` / `textValueOptionClass` / `textValueKey` overrides — same
// shape as the heavy mixin so the render output matches the Builder
// variant pixel-for-pixel for the same persisted options.
//
// The shared resolver (`option-calculation-mixin`, injected by
// `DynamicElementMixin`) is the only path these computeds use to read
// option leaves. No DOM measurement, no MutationObservers, no document
// listeners — every per-element hook in `text-element-mixin.js` is
// edit-mode-only and stays on the heavy mixin.

import BaseOption from "%/dynamic-component-editor/classes/base-option";
import normalizeFontSizeStyleValue from "%/dynamic-component-editor/helpers/font-size-style-value";
import { suppressesNavigation } from "%/dynamic-component-editor/helpers/builder-navigation-guard";
import { hasUnfilledPlaceholder } from "%/dynamic-component-editor/standard-elements/click-actions/resolve-context-href";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import BorderSides from "%/dynamic-component-editor/options/border-sides";
import BoxShadow from "%/dynamic-component-editor/options/box-shadow";
import ClickAction from "%/dynamic-component-editor/options/click-action";
import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";
import FontCase from "%/dynamic-component-editor/options/font-case";
import FontColor from "%/dynamic-component-editor/options/font-color";
import FontFamily from "%/dynamic-component-editor/options/font-family";
import FontSize from "%/dynamic-component-editor/options/font-size";
import FontStyle from "%/dynamic-component-editor/options/font-style";
import FontWeight from "%/dynamic-component-editor/options/font-weight";
import Label from "%/dynamic-component-editor/options/label";
import LayoutHeight from "%/dynamic-component-editor/options/layout-height";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import LineHeightMultiplier from "%/dynamic-component-editor/options/line-height-multiplier";
import LinkType from "%/dynamic-component-editor/options/link-type";
import { resolveContextualPageValue } from "%/dynamic-components/render-engine/options/click-actions/contextual-page-value";
import { isBuilderQuiet, pageLinkValueFor } from "%/dynamic-component-editor/standard-elements/click-actions/resolve-page-target";
import Margins from "%/dynamic-component-editor/options/margins";
import MaxVerticalLines from "%/dynamic-component-editor/options/max-vertical-lines";
import MaxWidth from "%/dynamic-component-editor/options/max-width";
import MinWidth from "%/dynamic-component-editor/options/min-width";
import Padding from "%/dynamic-component-editor/options/padding";
import { ContextHrefMixin } from "%/dynamic-component-editor/standard-elements/click-actions/context-href-mixin";
import TextAlign from "%/dynamic-component-editor/options/text-align";
import TextDecoration from "%/dynamic-component-editor/options/text-decoration";
import TextOption from "%/dynamic-component-editor/options/text-option";
import TextOverflow from "%/dynamic-component-editor/options/text-overflow";
import TextShadow from "%/dynamic-component-editor/options/text-shadow";
import TextWrap from "%/dynamic-component-editor/options/text-wrap";
import { GestureActionsMixin } from "%/dynamic-component-editor/standard-elements/click-actions/gesture-actions-mixin";

// Compiled "no border" shape iText's border-css helper derefs;
// mirrors the layout mixin's NO_BORDER_SIDES.
const NO_BORDER_SIDE = Object.freeze({ width: "0", color: "transparent", style: "solid" });
const NO_TEXT_BORDER_SIDES = Object.freeze({
  top: NO_BORDER_SIDE,
  right: NO_BORDER_SIDE,
  bottom: NO_BORDER_SIDE,
  left: NO_BORDER_SIDE,
});

export const TextElementRenderMixin = {
  mixins: [ContextHrefMixin, GestureActionsMixin],
  computed: {
    myWidth() {
      if (!this.getOptionValue(TextOverflow) && !this.getOptionValue(TextWrap)) {
        if (typeof this.getOptionValue(LayoutWidth) !== "number") {
          return "fill";
        }
      }
      return this.getOptionValue(LayoutWidth);
    },
    myHeight() {
      return this.getOptionValue(LayoutHeight);
    },
    myBorderSides() {
      // A missing BorderSides leaf must degrade to "no border" — the
      // template and iText's border-css helper deref each side, so undefined crashes.
      return this.getOptionValue(BorderSides) ?? NO_TEXT_BORDER_SIDES;
    },
    fullTextOptionsAttributes() {
      return {
        width: this.myWidth,
        height: this.myHeight,
        maxWith: this.getOptionValue(MaxWidth),
        minWith: this.getOptionValue(MinWidth),
        ...this.textOnlyAttributes,
        textStyleOverrides: this.textStyleOverrides,
      };
    },
    textOnlyAttributes() {
      return {
        overflow: this.getOptionValue(TextOverflow),
        maxVerticalLines: this.getOptionValue(MaxVerticalLines),
        wrap: this.getOptionValue(TextWrap),
        textAlign: this.getOptionValue(TextAlign),
        textCase: this.getOptionValue(FontCase),
        lineHeightMultiplier: this.getOptionValue(LineHeightMultiplier),
        textStyleOverrides: this.textOnlyStyleOverrides,
      };
    },
    spacingStyleOverrides() {
      return {
        paddingTop: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "top") || "none",
        paddingBottom: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "bottom") || "none",
        paddingLeft: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "left") || "none",
        paddingRight: this.getOptionValue(Padding, BaseOption.$states.DEFAULT, "right") || "none",
        marginTop: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "top") || "none",
        marginBottom: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "bottom") || "none",
        marginLeft: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "left") || "none",
        marginRight: this.getOptionValue(Margins, BaseOption.$states.DEFAULT, "right") || "none",
        paddingHoverTop: this.getOptionValue(Padding, BaseOption.$states.HOVER, "top") || "none",
        paddingHoverBottom: this.getOptionValue(Padding, BaseOption.$states.HOVER, "bottom") || "none",
        paddingHoverLeft: this.getOptionValue(Padding, BaseOption.$states.HOVER, "left") || "none",
        paddingHoverRight: this.getOptionValue(Padding, BaseOption.$states.HOVER, "right") || "none",
        marginHoverTop: this.getOptionValue(Margins, BaseOption.$states.HOVER, "top") || "none",
        marginHoverBottom: this.getOptionValue(Margins, BaseOption.$states.HOVER, "bottom") || "none",
        marginHoverLeft: this.getOptionValue(Margins, BaseOption.$states.HOVER, "left") || "none",
        marginHoverRight: this.getOptionValue(Margins, BaseOption.$states.HOVER, "right") || "none",
      };
    },
    textOnlyStyleOverrides() {
      return {
        standardFontFamily: this.getOptionValue(FontFamily) || "standard",
        standardFontSize: normalizeFontSizeStyleValue(this.getOptionValue(FontSize), { element: "TextElement", option: "font-size" }),
        standardFontColor: this.getOptionValue(FontColor) || "standard",
        standardFontWeight: this.getOptionValue(FontWeight) || "standard",
        standardHoverFontFamily: this.getOptionValue(FontFamily, BaseOption.$states.HOVER) || "standard",
        standardHoverFontSize: normalizeFontSizeStyleValue(this.getOptionValue(FontSize, BaseOption.$states.HOVER), { element: "TextElement", option: "hover.font-size" }),
        standardHoverFontColor: this.getOptionValue(FontColor, BaseOption.$states.HOVER) || "standard",
        standardHoverFontWeight: this.getOptionValue(FontWeight, BaseOption.$states.HOVER) || "standard",
        textDecoration: this.getOptionValue(TextDecoration),
        textDecorationHover: this.getOptionValue(TextDecoration, BaseOption.$states.HOVER),
        textStyle: this.getOptionValue(FontStyle),
        textStyleHover: this.getOptionValue(FontStyle, BaseOption.$states.HOVER),
      };
    },
    textStyleOverrides() {
      return {
        standardFontFamily: this.getOptionValue(FontFamily) || "standard",
        standardFontSize: normalizeFontSizeStyleValue(this.getOptionValue(FontSize), { element: "TextElement", option: "font-size" }),
        standardFontColor: this.getOptionValue(FontColor) || "standard",
        standardFontWeight: this.getOptionValue(FontWeight) || "standard",
        standardHoverFontFamily: this.getOptionValue(FontFamily, BaseOption.$states.HOVER) || "standard",
        standardHoverFontSize: normalizeFontSizeStyleValue(this.getOptionValue(FontSize, BaseOption.$states.HOVER), { element: "TextElement", option: "hover.font-size" }),
        standardHoverFontColor: this.getOptionValue(FontColor, BaseOption.$states.HOVER) || "standard",
        standardHoverFontWeight: this.getOptionValue(FontWeight, BaseOption.$states.HOVER) || "standard",
        ...this.spacingStyleOverrides,
        backgroundColor: this.getOptionValue(BackgroundColor),
        textDecoration: this.getOptionValue(TextDecoration),
        textDecorationHover: this.getOptionValue(TextDecoration, BaseOption.$states.HOVER),
        textStyle: this.getOptionValue(FontStyle),
        textStyleHover: this.getOptionValue(FontStyle, BaseOption.$states.HOVER),
        hoverBackgroundColor: this.getOptionValue(BackgroundColor, BaseOption.$states.HOVER),
        boxShadow: this.getOptionValue(BoxShadow),
        boxShadowHover: this.getOptionValue(BoxShadow, BaseOption.$states.HOVER),
        textShadow: this.getOptionValue(TextShadow),
        textShadowHover: this.getOptionValue(TextShadow, BaseOption.$states.HOVER),
      };
    },
    textValueOptionClass() {
      return TextOption;
    },
    textValueKey() {
      return undefined;
    },
    textOptionValue() {
      let textValue = this.getOptionValue(this.textValueOptionClass) || "";
      if (this.textValueKey && textValue) {
        if (this.textValueKey === "label") {
          // A present label wins even if hasUpdated was lost; fall back to the
          // legacy Label option only when the label is genuinely empty.
          const label = textValue[this.textValueKey];
          const hasLabel = typeof label === "string" && label.length > 0;
          textValue = textValue.hasUpdated || hasLabel
            ? label
            : this.getOptionValue(Label);
        } else {
          textValue = textValue[this.textValueKey];
        }
      }
      return textValue;
    },
    // Plain-text projection of the persisted text-option for the iText
    // render. Rich-text formatting (ranges) is preserved on the leaf and
    // surfaces the moment the user selects the element and the Builder
    // variant takes over — the production render shows the body text
    // verbatim, which is what every consumer of an unselected canvas
    // element actually needs.
    renderedText() {
      const value = this.textOptionValue;
      if (typeof value === "string") {
        return value;
      }
      if (value && typeof value === "object" && typeof value.body_text === "string") {
        return value.body_text;
      }
      return "";
    },
    // Empty-state hint surfaced in `editMode` only. The Production variant
    // also mounts on the builder canvas (default frame, unselected
    // element), where a `TextOption.defaultValue === ""` leaf collapses
    // the iText to 0×0 and the element becomes invisible / unselectable.
    // Production variants are shared across branded sub-types, so we
    // don't get the Builder's per-alias `elementPanelSettings.alias` —
    // fall back to a generic string.
    placeholderText() {
      return this.$options.elementPanelSettings?.alias
        || this.$options.elementPanelSettings?.displayName
        || "Add text";
    },
    // Click-action chain — clickable text on the canvas continues to
    // route through the normal anchor flow even on production variants.
    actionType() {
      return this.getOptionValue(ClickAction)?.action;
    },
    linkType() {
      return this.getOptionValue(ClickAction)?.linkType;
    },
    openInNewTab() {
      return this.getOptionValue(ClickAction)?.openInNewTab;
    },
    clickValue() {
      const clickAction = this.getOptionValue(ClickAction);
      const urlValue = this.linkType === LinkType.LINK_TYPE_PAGE
        ? pageLinkValueFor(clickAction, clickAction?.value, this.scopedProvidedContexts, { quiet: isBuilderQuiet(this) })
        : clickAction?.value;
      if (!this.linkType || !urlValue) {
        return;
      }
      switch (this.linkType) {
        case LinkType.LINK_TYPE_HYPERLINK:
          return urlValue;
        case LinkType.LINK_TYPE_PAGE: {
          const contextHref = this.contextResolvedHref(
            this.getOptionValue(ClickAction), urlValue, this.links.web,
          );
          if (contextHref) {
            return contextHref;
          }
          const resolved = resolveContextualPageValue(urlValue);
          if (resolved === null || resolved === undefined) {
            return;
          }
          const stripped = resolved.startsWith("/") ? resolved.substring(1) : resolved;
          const pageHref = `${this.links.web}/${stripped}`;
          return hasUnfilledPlaceholder(pageHref) ? undefined : pageHref;
        }
        case LinkType.LINK_TYPE_STORY:
          return `${this.links.web}/story/${urlValue}`;
        case LinkType.LINK_TYPE_HASHTAG:
          return `${this.links.web}/stories/${urlValue}`;
        default:
          throw new DynamicComponentError("Url type not found", {
            linkType: this.linkType,
            urlValue,
          });
      }
    },
    // Inline rather than a scoped class: subclasses (Label, Heading, ...) inherit
    // this template through `extends` but render under their own scope id.
    clickableStyle() {
      return this.hasClickValue ? { cursor: "pointer" } : null;
    },
    hasClickValue() {
      return this.clickValue !== undefined && this.clickValue !== null && this.clickValue.length > 0;
    },
  },
  methods: {
    handleClickAction() {
      if (this.gestureOwnsTheClick || suppressesNavigation(this)) {
        return;
      }
      if (this.actionType !== "goToUrl" || !this.clickValue) {
        return;
      }
      const a = document.createElement("a");
      if (this.openInNewTab) {
        a.target = "_blank";
      }
      a.href = this.clickValue;
      // A detached anchor ignores target="_blank" — it has to be in the
      // document for the browser to honour it.
      if (this.openInNewTab) {
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }
      a.click();
    },
  },
};
